import React, { useEffect, useRef } from 'react';
import { X } from 'lucide-react';

/**
 * ManifestoModal - Full-page modal displaying the PropShare Manifesto
 * Features scroll-driven navigation index that highlights current section
 * 
 * Based on CSS Scroll-Driven Animations pattern
 * @param {boolean} isOpen - Whether modal is visible
 * @param {function} onClose - Close handler
 */
const ManifestoModal = ({ isOpen, onClose }) => {
  const scrollContainerRef = useRef(null);
  const [scrollProgress, setScrollProgress] = React.useState(0);
  const [isIndexExpanded, setIsIndexExpanded] = React.useState(false);

  // Close on Escape key
  useEffect(() => {
    if (!isOpen) return;

    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }

    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  // Close index when clicking outside
  useEffect(() => {
    if (!isOpen || !isIndexExpanded) return;

    const handleClickOutside = (event) => {
      // Check if click is outside the index container
      const indexContainer = event.target.closest('.index-container');
      if (!indexContainer) {
        setIsIndexExpanded(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen, isIndexExpanded]);

  // Track scroll progress
  useEffect(() => {
    if (!isOpen) return;

    const handleScroll = () => {
      const container = scrollContainerRef.current;
      if (!container) return;

      // Calculate scroll percentage
      const scrollTop = container.scrollTop;
      const scrollHeight = container.scrollHeight - container.clientHeight;
      const progress = scrollHeight > 0 ? (scrollTop / scrollHeight) * 100 : 0;
      setScrollProgress(Math.round(progress));
    };

    const container = scrollContainerRef.current;
    if (container) {
      container.addEventListener('scroll', handleScroll);
      // Initial check
      handleScroll();
    }

    return () => {
      if (container) {
        container.removeEventListener('scroll', handleScroll);
      }
    };
  }, [isOpen]);

  if (!isOpen) return null;

  // Scroll to section when index item is clicked
  const scrollToSection = (sectionId) => {
    const section = document.getElementById(sectionId);
    if (section && scrollContainerRef.current) {
      const container = scrollContainerRef.current;
      const sectionTop = section.offsetTop;
      container.scrollTo({
        top: sectionTop - 100, // Offset for better visibility
        behavior: 'smooth'
      });
    }
  };

  return (
    <div className="fixed inset-0 z-[200] animate-fadeIn" style={{ backgroundColor: '#ffffff' }}>
      {/* Grid Background */}
      <div 
        className="absolute inset-0 pointer-events-none z-0"
        style={{
          backgroundImage: `
            repeating-linear-gradient(0deg, #d1d5db 0px, #d1d5db 1px, transparent 1px, transparent 40px),
            repeating-linear-gradient(90deg, #d1d5db 0px, #d1d5db 1px, transparent 1px, transparent 40px)
          `,
          opacity: 0.5,
          maskImage: 'linear-gradient(135deg, rgba(0,0,0,1) 0%, rgba(0,0,0,0.3) 40%, rgba(0,0,0,0) 70%)',
          WebkitMaskImage: 'linear-gradient(135deg, rgba(0,0,0,1) 0%, rgba(0,0,0,0.3) 40%, rgba(0,0,0,0) 70%)'
        }}
      />
      
      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        .animate-fadeIn {
          animation: fadeIn 0.3s ease-out;
        }
      `}</style>
      {/* Close Button */}
      <button
        onClick={onClose}
        className="fixed top-6 right-6 z-30 w-12 h-12 bg-white rounded-full shadow-lg flex items-center justify-center hover:bg-gray-100 transition-colors"
        aria-label="Close manifesto"
      >
        <X className="w-6 h-6 text-gray-700" />
      </button>

      {/* Scroll Container */}
      <div
        ref={scrollContainerRef}
        className="h-full overflow-y-auto scroll-smooth relative z-10"
        style={{ scrollPaddingTop: '100px' }}
      >
        <div className="max-w-2xl mx-auto px-8 py-12 pb-32">
          {/* Header */}
          <header className="mb-16 text-center">
            <h1 className="text-5xl font-bold text-gray-900 mb-4">
              The PropShare Manifesto
            </h1>
            <p className="text-xl text-gray-600">
              Building a sustainable future for theater through shared resources
            </p>
          </header>

          {/* Content Sections - Placeholder content for demo */}
          <article className="max-w-none text-left">
            <section id="section-1" className="mb-20 scroll-mt-24">
              <h2 className="text-3xl font-bold text-gray-900 mb-6">01. Our Vision</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                Theater has always been about collaboration, creativity, and community. Yet when it comes to the physical resources that bring our stories to life, we've been working in silos—each company purchasing, storing, and eventually disposing of props that could serve countless productions.
              </p>
              <p className="text-gray-700 leading-relaxed mb-4">
                PropShare Network reimagines this model. We believe that every prop has more than one story to tell, and every theater company can benefit from the collective resources of the community.
              </p>
              <p className="text-gray-700 leading-relaxed">
                This isn't just about saving money or storage space—though those are wonderful benefits. This is about building a more sustainable, connected, and resilient theater ecosystem where resources flow where they're needed most.
              </p>
            </section>

            <section id="section-2" className="mb-20 scroll-mt-24">
              <h2 className="text-3xl font-bold text-gray-900 mb-6">02. The Problem We're Solving</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                Every theater company faces the same challenges: limited budgets, constrained storage, and the constant need for specific props that might only be used once. Meanwhile, warehouses overflow with items gathering dust, and landfills receive perfectly good props that have outlived their original production.
              </p>
              <p className="text-gray-700 leading-relaxed mb-4">
                The traditional approach—buy, store, discard—is unsustainable financially and environmentally. It isolates companies, increases costs, and wastes both resources and potential.
              </p>
              <p className="text-gray-700 leading-relaxed">
                There had to be a better way. PropShare Network is that better way.
              </p>
            </section>

            <section id="section-3" className="mb-20 scroll-mt-24">
              <h2 className="text-3xl font-bold text-gray-900 mb-6">03. Our Core Principles</h2>
              <div className="space-y-6">
                <div>
                  <h3 className="text-2xl font-semibold text-gray-900 mb-3">i. Community Over Competition</h3>
                  <p className="text-gray-700 leading-relaxed">
                    Theater companies aren't competing for audiences when they share resources—they're strengthening the entire ecosystem. A rising tide lifts all boats, and a connected community creates more opportunities for everyone.
                  </p>
                </div>
                <div>
                  <h3 className="text-2xl font-semibold text-gray-900 mb-3">ii. Sustainability in Action</h3>
                  <p className="text-gray-700 leading-relaxed">
                    Every shared prop is one less item manufactured, purchased, transported, and eventually discarded. By maximizing the lifecycle of physical resources, we reduce our environmental impact while maintaining artistic excellence.
                  </p>
                </div>
                <div>
                  <h3 className="text-2xl font-semibold text-gray-900 mb-3">iii. Accessibility and Equity</h3>
                  <p className="text-gray-700 leading-relaxed">
                    Smaller companies and emerging organizations shouldn't be limited by their prop budgets. PropShare levels the playing field, giving every company access to professional-grade resources regardless of their size or financial capacity.
                  </p>
                </div>
                <div>
                  <h3 className="text-2xl font-semibold text-gray-900 mb-3">iv. Transparency and Trust</h3>
                  <p className="text-gray-700 leading-relaxed">
                    Our platform is built on clear communication, fair terms, and mutual respect. Every transaction is tracked, every agreement is honored, and every company knows exactly what to expect.
                  </p>
                </div>
              </div>
            </section>

            <section id="section-4" className="mb-20 scroll-mt-24">
              <h2 className="text-3xl font-bold text-gray-900 mb-6">04. How It Works</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                PropShare Network connects theater companies with warehouse inventories through an intuitive digital platform. Search for what you need, request it from local warehouses, and coordinate pickup or delivery—all in one place.
              </p>
              <p className="text-gray-700 leading-relaxed mb-4">
                Warehouse managers maintain control over their inventory, set availability schedules, and track loans in real-time. Requesting companies gain access to thousands of props they couldn't afford to purchase or store themselves.
              </p>
              <p className="text-gray-700 leading-relaxed">
                The system handles logistics, communication, and accountability, so you can focus on what matters: creating great theater.
              </p>
            </section>

            <section id="section-5" className="mb-20 scroll-mt-24">
              <h2 className="text-3xl font-bold text-gray-900 mb-6">05. The Impact</h2>
              <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-8 mb-6">
                <h3 className="text-2xl font-semibold text-gray-900 mb-4">Environmental</h3>
                <p className="text-gray-700 leading-relaxed">
                  By sharing props instead of purchasing new ones, we dramatically reduce manufacturing demand, transportation emissions, and landfill waste. Every shared prop is a victory for sustainability.
                </p>
              </div>
              <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl p-8 mb-6">
                <h3 className="text-2xl font-semibold text-gray-900 mb-4">Community</h3>
                <p className="text-gray-700 leading-relaxed">
                  PropShare creates connections between organizations that might never otherwise collaborate. These relationships foster knowledge sharing, artistic partnerships, and a stronger theater community.
                </p>
              </div>
              <div className="bg-gradient-to-r from-yellow-50 to-amber-50 rounded-xl p-8">
                <h3 className="text-2xl font-semibold text-gray-900 mb-4">Financial</h3>
                <p className="text-gray-700 leading-relaxed">
                  Theater budgets stretch further when companies share resources. Money saved on props can be invested in artists, education, and community programs—directly strengthening the art form.
                </p>
              </div>
            </section>

            <section id="section-6" className="mb-20 scroll-mt-24">
              <h2 className="text-3xl font-bold text-gray-900 mb-6">06. Join the Movement</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                PropShare Network is more than a tool—it's a shift in how we think about theatrical resources. It's a commitment to collaboration over isolation, sustainability over waste, and community over competition.
              </p>
              <p className="text-gray-700 leading-relaxed mb-4">
                Whether you're a small community theater or a major regional company, you have something to contribute and something to gain. Together, we're building a future where every prop tells multiple stories, every company has access to what they need, and the theater community thrives through shared success.
              </p>
              <p className="text-gray-700 leading-relaxed text-xl font-semibold">
                Welcome to PropShare Network. Let's share the stage.
              </p>
            </section>
          </article>
        </div>
      </div>

      {/* Bottom Progress Bar with Expandable Index */}
      <div className="fixed bottom-0 left-0 right-0 flex justify-center pointer-events-none z-20 px-4">
        <div className="pointer-events-auto w-full max-w-xs index-container">
          {/* Expanded Section List */}
          <div 
            className={`bg-white/95 backdrop-blur-sm rounded-t-xl shadow-2xl transition-all duration-300 overflow-hidden ${
              isIndexExpanded ? 'max-h-64 opacity-100 mb-0' : 'max-h-0 opacity-0 mb-0'
            }`}
          >
            <div className="p-3 pb-2 max-h-56 overflow-y-auto">
              <ul className="space-y-0.5">
                {[
                  { id: 'section-1', label: 'Our Vision' },
                  { id: 'section-2', label: 'The Problem' },
                  { id: 'section-3', label: 'Core Principles' },
                  { id: 'section-4', label: 'How It Works' },
                  { id: 'section-5', label: 'The Impact' },
                  { id: 'section-6', label: 'Join Us' },
                ].map((section, index) => {
                  const number = String(index + 1).padStart(2, '0');
                  return (
                    <li key={section.id}>
                      <button
                        onClick={() => {
                          scrollToSection(section.id);
                          setIsIndexExpanded(false);
                        }}
                        className="w-full text-left px-2 py-1.5 rounded-md transition-all duration-200 hover:bg-blue-50 text-gray-700 hover:text-blue-700 text-xs"
                      >
                        <span className="font-medium mr-2">{number}.</span>
                        {section.label}
                      </button>
                    </li>
                  );
                })}
              </ul>
            </div>
          </div>

          {/* Progress Bar - Always Visible */}
          <button
            onClick={() => setIsIndexExpanded(!isIndexExpanded)}
            className="relative w-full bg-blue-600 text-white px-6 py-3 rounded-t-xl shadow-2xl hover:bg-blue-700 transition-all flex items-center justify-center gap-3 overflow-hidden"
          >
            {/* Background progress fill */}
            <div 
              className="absolute inset-0 bg-blue-500 transition-all duration-300"
              style={{ width: `${scrollProgress}%` }}
            />
            
            {/* Content */}
            <div className="relative z-10 flex items-center gap-3">
              <div className="flex items-center gap-1.5">
                <span className="text-xs font-semibold uppercase tracking-wide">Index</span>
                <svg 
                  className={`w-4 h-4 transition-transform duration-300 ${
                    isIndexExpanded ? 'rotate-180' : ''
                  }`}
                  fill="none" 
                  stroke="currentColor" 
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                </svg>
              </div>
              <span className="text-xl font-bold">{scrollProgress}%</span>
            </div>
          </button>
        </div>
      </div>
    </div>
  );
};

export default ManifestoModal;
